import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { withoutBidiControls as plain } from "../snippets/bidi";
import TargetBreaches from "./TargetBreaches";
import TargetHoldingsList from "./TargetHoldingsList";
import { formatDay, taskBoardPath, whoName } from "./lib/format";
import { kindLabel } from "./lib/target";

/**
 * The current target (spec §5.4, D4): the approved version — every holding
 * with its weight, reason and primary thesis, and cash — the cap breaches
 * it was approved with and their overrides, and the sync task approving it
 * created for the executor (walkthrough 8). Before the first approval it
 * says how one arrives.
 */
function CurrentTargetPanel({ version }) {
  if (!version) {
    return (
      <section aria-label="Current target" className="rounded-lg border bg-card p-4">
        <h2 className="mt-0 mb-1 text-lg font-semibold">
          Current target
        </h2>
        <p className="m-0 text-sm">
          No target approved yet. An agent drafts the first one on a board task, and you approve it here. Nothing
          trades until then.
        </p>
      </section>
    );
  }
  const breaches = Array.isArray(version.cap_breaches) ? version.cap_breaches : [];
  const decided = formatDay(version.decided_at);
  return (
    <section aria-label="Current target" className="rounded-lg border bg-card p-4">
      <h2 className="mt-0 mb-1 text-lg font-semibold">
        Current target — v{version.number}
      </h2>
      <p className="m-0 font-display text-xl font-semibold wrap-anywhere">{plain(version.title)}</p>
      <p className="m-0 text-sm text-muted-foreground">
        {kindLabel(version.kind)} · Approved by {whoName(version.decided_by)}
        {decided ? ` on ${decided}` : ""}
      </p>
      {version.decision_reason && (
        <p className="m-0 text-sm wrap-anywhere">Why: {plain(version.decision_reason)}</p>
      )}
      {version.sync_task && (
        <p className="m-0 text-sm wrap-anywhere">
          Sync task:{" "}
          <Link
            to={taskBoardPath(version.sync_task.id)}
            className="inline-flex min-h-11 items-center underline underline-offset-2 md:min-h-0"
          >
            {plain(version.sync_task.title)} (task #{version.sync_task.id})
          </Link>
        </p>
      )}
      <div className="mt-3">
        <TargetHoldingsList version={version} label={`Holdings in v${version.number}`} />
      </div>
      {breaches.length > 0 && (
        <>
          <h3 className="mt-3 mb-1 text-base font-semibold">Approved with these cap breaches</h3>
          <TargetBreaches breaches={breaches} version={version} />
        </>
      )}
    </section>
  );
}

CurrentTargetPanel.propTypes = {
  version: PropTypes.shape({
    number: PropTypes.number.isRequired,
    title: PropTypes.string,
    kind: PropTypes.string,
    decided_by: PropTypes.object,
    decided_at: PropTypes.string,
    decision_reason: PropTypes.string,
    sync_task: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }),
    cap_breaches: PropTypes.arrayOf(PropTypes.object),
    holdings: PropTypes.arrayOf(PropTypes.object),
    cash_weight: PropTypes.string.isRequired,
  }),
};

export default CurrentTargetPanel;
